import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, Alert } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { ArtifactService } from '../../services/api/artifactService';
import { ArtifactTree } from '../../types/artifacts';
import { useArtifactTree } from '../../contexts/ArtifactTreeContext';
import { useUser } from '../../contexts/UserContext';
import { isPlatformAdministrator } from '../AppShell/appNavAccess';
import { ArtifactTree as ArtifactTreeComponent } from './ArtifactTree';
import { WorkflowDashboard } from '../PrimeModel/WorkflowDashboard';
import { ProcessCentricView } from '../PrimeModel/ProcessCentricView';
import { BootstrapTrackingTab } from './BootstrapTrackingTab';
import { Select } from 'antd';
import { fetchArtifacts, checkRootStatus } from '../../store/slices/ArtifactSlice';
import { fetchBusinessModels } from '../../store/slices/BusinessModelSlice';
import { AppDispatch } from '../../store';
import { usePublishSurfaceChatContext } from '../../hooks/usePublishSurfaceChatContext';
import { companyArtifactsViewLabel } from '../../utils/surfaceRouteLabels';
import {
  COMPANY_ARTIFACTS_SELECT_PATH,
  isCompanyArtifactModelSlug,
} from '../../utils/companyArtifactRoutes';
import { resolveTreeForModelSlug } from '../../utils/resolveTreeForModelSlug';
import { resolveCompanyForModelSlug } from '../../utils/resolveCompanyIdForModelSlug';
import styles from './CompanyArtifactLayout.module.css';

const { Option } = Select;

const CompanyArtifactLayout: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { modelSlug: routeModelSlug } = useParams<{ modelSlug?: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedTree, setSelectedTree } = useArtifactTree();
  const { selectedCompany, availableCompanies, user } = useUser();
  const showBootstrapTools = isPlatformAdministrator(user);
  const [activeView, setActiveView] = useState<'tree' | 'workflow' | 'processes' | 'bootstrap'>('tree');
  const [trees, setTrees] = useState<ArtifactTree[]>([]);
  const [loadingTrees, setLoadingTrees] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const modelSlug = isCompanyArtifactModelSlug(routeModelSlug) ? routeModelSlug.trim() : undefined;

  const modelCompany = useMemo(
    () => resolveCompanyForModelSlug(modelSlug, selectedCompany, availableCompanies),
    [modelSlug, selectedCompany, availableCompanies]
  );

  const treeCompanyId = modelCompany.companyId;

  useEffect(() => {
    if (!modelSlug) {
      navigate(COMPANY_ARTIFACTS_SELECT_PATH, { replace: true });
    }
  }, [modelSlug, navigate]);

  useEffect(() => {
    if (!modelSlug) return;

    const loadTrees = async () => {
      try {
        setLoadingTrees(true);
        const accessibleCompanyIds = availableCompanies.map((uc) => uc.company.id);
        const response = await ArtifactService.getTrees(treeCompanyId, accessibleCompanyIds);
        setTrees(response);
        const match = resolveTreeForModelSlug(response, modelSlug, { companyId: treeCompanyId });
        if (match) {
          setSelectedTree(match);
        } else {
          setSelectedTree(null);
        }
      } catch (error) {
        console.error('Failed to load artifact trees:', error);
      } finally {
        setLoadingTrees(false);
      }
    };

    void loadTrees();
  }, [modelSlug, treeCompanyId, availableCompanies, setSelectedTree]);

  const artifactsSurfaceContext = useMemo(() => {
    const companyName = modelCompany.companyName ?? selectedCompany?.name ?? 'Company';
    const viewLabel = companyArtifactsViewLabel(activeView);
    return {
      surfaceId: `company-artifacts:${treeCompanyId ?? 'none'}:${modelSlug ?? 'none'}:${activeView}`,
      surfaceLabel: `${companyName} · Company artifacts`,
      route: pathname,
      summary: modelSlug
        ? `${modelSlug}${selectedTree ? ` · ${selectedTree.name}` : ''} · ${viewLabel}`
        : 'Select company model',
      metadata: {
        companyId: treeCompanyId,
        modelSlug,
        treeId: selectedTree?.id,
        activeView,
      },
    };
  }, [
    activeView,
    pathname,
    selectedCompany?.id,
    selectedCompany?.name,
    treeCompanyId,
    modelCompany.companyName,
    selectedTree?.id,
    selectedTree?.name,
    modelSlug,
  ]);

  usePublishSurfaceChatContext(artifactsSurfaceContext);

  const handleTreeChange = useCallback(
    (treeId: string) => {
      const tree = trees.find((t) => t.id === treeId);
      if (tree) {
        setSelectedTree(tree);
      }
    },
    [trees, setSelectedTree]
  );

  const handleArtifactClick = useCallback((_artifactId: string) => {
    setActiveView('tree');
  }, []);

  const handleRefresh = useCallback(async () => {
    if (!selectedTree) return;

    try {
      setRefreshing(true);
      const treeId = selectedTree.id;
      const companyId = treeCompanyId || undefined;

      await Promise.all([
        dispatch(fetchArtifacts({ treeId, companyId })),
        dispatch(checkRootStatus({ treeId, companyId })),
        dispatch(fetchBusinessModels(treeId)),
      ]);
    } catch (error) {
      console.error('Failed to refresh artifact tree:', error);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, selectedTree, treeCompanyId]);

  const content = useMemo(() => {
    if (activeView === 'workflow') {
      return (
        <div className={styles.companyArtifactContentContainer}>
          <WorkflowDashboard
            onArtifactClick={handleArtifactClick}
            modelType="company"
            artifactTreeId={selectedTree?.id}
            companyId={treeCompanyId}
          />
        </div>
      );
    }

    if (activeView === 'processes') {
      return (
        <div className={styles.companyArtifactContentContainer}>
          <ProcessCentricView
            selectedArtifactId={undefined}
            artifactTreeId={selectedTree?.id}
            companyId={treeCompanyId}
            onProcessClick={(processId) => {
              console.log('Process clicked:', processId);
            }}
          />
        </div>
      );
    }

    if (activeView === 'bootstrap' && showBootstrapTools) {
      return (
        <div className={styles.companyArtifactContentContainer}>
          <BootstrapTrackingTab companyId={treeCompanyId} />
        </div>
      );
    }

    if (activeView === 'tree' && !selectedTree) {
      return (
        <div className={styles.companyArtifactContentContainer}>
          <Alert
            type="info"
            showIcon
            message={`Workspace model: ${modelSlug}`}
            description="This kryspace model is selected. A legacy database artifact tree is not linked to this slug yet — artifact editing will appear here once a tree is bootstrapped or linked via tree_metadata.kryspace_slug."
          />
        </div>
      );
    }

    return (
      <div className={styles.companyArtifactContentContainer}>
        <ArtifactTreeComponent />
      </div>
    );
  }, [activeView, handleArtifactClick, selectedTree, modelSlug, treeCompanyId, showBootstrapTools]);

  if (!modelSlug || loadingTrees) {
    return (
      <div className={styles.companyArtifactLayout}>
        <div className={styles.loading}>Loading company model...</div>
      </div>
    );
  }

  return (
    <div className={styles.companyArtifactLayout}>
      <div className={styles.companyArtifactHeader}>
        <h1>Company Model Manager</h1>
        <div className={styles.headerControls}>
          <Button type="link" onClick={() => navigate(COMPANY_ARTIFACTS_SELECT_PATH)} style={{ marginRight: 8 }}>
            Change model
          </Button>
          {selectedTree ? (
            <div className={styles.treeSelector} style={{ marginRight: '16px' }}>
              <Select
                value={selectedTree.id}
                onChange={handleTreeChange}
                loading={loadingTrees}
                style={{ width: 200 }}
                placeholder="Select artifact tree"
              >
                {trees.map((tree) => (
                  <Option key={tree.id} value={tree.id}>
                    {tree.name}
                  </Option>
                ))}
              </Select>
            </div>
          ) : (
            <span className={styles.modelSlugBadge} style={{ marginRight: '16px' }}>
              Model: <code>{modelSlug}</code>
            </span>
          )}
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={refreshing}
            disabled={!selectedTree}
            style={{ marginRight: '16px' }}
            title="Refresh artifact tree"
          >
            Refresh
          </Button>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.toggleButton} ${activeView === 'tree' ? styles.active : ''}`}
              onClick={() => setActiveView('tree')}
            >
              Artifact Tree
            </button>
            <button
              className={`${styles.toggleButton} ${activeView === 'workflow' ? styles.active : ''}`}
              onClick={() => setActiveView('workflow')}
            >
              Workflow
            </button>
            <button
              className={`${styles.toggleButton} ${activeView === 'processes' ? styles.active : ''}`}
              onClick={() => setActiveView('processes')}
            >
              Processes
            </button>
            {showBootstrapTools ? (
              <button
                className={`${styles.toggleButton} ${activeView === 'bootstrap' ? styles.active : ''}`}
                onClick={() => setActiveView('bootstrap')}
              >
                Bootstrap Tracking
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <div className={styles.companyArtifactContent}>{content}</div>
    </div>
  );
});

CompanyArtifactLayout.displayName = 'CompanyArtifactLayout';

export default CompanyArtifactLayout;
